import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

const CardWrapper = styled(Box)(({ theme, accentcolor }) => ({
  background: '#ffffff',
  borderRadius: '12px',
  padding: '16px',
  minWidth: '100%',
  flex: 1,
  cursor: 'pointer',
  transition: 'all 0.25s ease',
  border: '1px solid #e5e7eb',
  position: 'relative',
  overflow: 'hidden',
  WebkitTapHighlightColor: 'transparent',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: accentcolor || 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
    borderRadius: '12px 12px 0 0'
  },
  '&:active': {
    transform: 'scale(0.98)'
  },
  [theme.breakpoints.up('sm')]: {
    padding: '18px',
    borderRadius: '14px',
    '&::before': {
      borderRadius: '14px 14px 0 0'
    }
  },
  [theme.breakpoints.up('md')]: {
    padding: '20px',
    minWidth: '180px',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 24px rgba(0, 0, 0, 0.1)',
      borderColor: '#d1d5db'
    },
    '&:active': {
      transform: 'translateY(-4px)'
    }
  }
}));

const CardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '12px',
  [theme.breakpoints.up('sm')]: {
    marginBottom: '14px'
  },
  [theme.breakpoints.up('md')]: {
    marginBottom: '16px'
  }
}));

const IconBox = styled(Box)(({ theme, bgcolor }) => ({
  width: '32px',
  height: '32px',
  borderRadius: '8px',
  backgroundColor: bgcolor || '#eff6ff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '14px',
  [theme.breakpoints.up('sm')]: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    fontSize: '16px'
  }
}));

const ChangeTag = styled(Box)(({ ispositive }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 8px',
  borderRadius: '6px',
  backgroundColor: ispositive === 'true' ? '#ecfdf5' : '#fef2f2',
  fontSize: '11px',
  fontWeight: 600,
  fontFamily: 'Inter, sans-serif',
  color: ispositive === 'true' ? '#059669' : '#dc2626'
}));

const MetricTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '11px',
  fontWeight: 500,
  color: '#6b7280',
  marginBottom: '6px',
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '12px',
    marginBottom: '7px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '13px',
    marginBottom: '8px'
  }
}));

const MetricValue = styled(Typography)(({ theme, valuecolor }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '24px',
  fontWeight: 700,
  color: valuecolor || '#111827',
  lineHeight: 1,
  marginBottom: '4px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '28px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '32px'
  }
}));

const MetricSubtext = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '11px',
  color: '#9ca3af',
  marginTop: '6px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '12px',
    marginTop: '8px'
  }
}));

const getMetricIcon = (title) => {
  const icons = {
    'Local Views': '👁️',
    'Calls from GBP': '📞',
    'Direction Requests': '📍',
    'Website Clicks': '🌐',
    'New Reviews': '⭐',
    'Organic Clicks This Month': '🖱️',
    'Avg. Google Search Volume': '🔍'
  };
  return icons[title] || '📊';
};

const getAccentColor = (title) => {
  const colors = {
    'Local Views': 'linear-gradient(90deg, #3b82f6, #6366f1)',
    'Calls from GBP': 'linear-gradient(90deg, #10b981, #14b8a6)',
    'Direction Requests': 'linear-gradient(90deg, #f59e0b, #f97316)',
    'Website Clicks': 'linear-gradient(90deg, #8b5cf6, #a855f7)',
    'New Reviews': 'linear-gradient(90deg, #ec4899, #f43f5e)',
    'Organic Clicks This Month': 'linear-gradient(90deg, #06b6d4, #0891b2)',
    'Avg. Google Search Volume': 'linear-gradient(90deg, #f43f5e, #e11d48)'
  };
  return colors[title] || 'linear-gradient(90deg, #3b82f6, #8b5cf6)';
};

const getIconBgColor = (title) => {
  const colors = {
    'Local Views': '#eff6ff',
    'Calls from GBP': '#ecfdf5',
    'Direction Requests': '#fef3c7',
    'Website Clicks': '#f3e8ff',
    'New Reviews': '#fce7f3',
    'Organic Clicks This Month': '#ecfeff',
    'Avg. Google Search Volume': '#fff1f2'
  };
  return colors[title] || '#eff6ff';
};

const MetricCard = ({ 
  title, 
  value, 
  change, 
  backgroundColor, 
  valueColor,
  isLarge = false,
  onClick
}) => {
  const isPositive = change >= 0;
  const displayChange = change !== undefined && change !== null;
  
  return (
    <CardWrapper 
      accentcolor={getAccentColor(title)} 
      onClick={onClick}
    >
      <CardHeader>
        <IconBox bgcolor={getIconBgColor(title)}>
          {getMetricIcon(title)}
        </IconBox>
      </CardHeader>
      
      <MetricTitle>{title}</MetricTitle>
      <MetricValue valuecolor={valueColor}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </MetricValue>
      <MetricSubtext>vs last period</MetricSubtext>
    </CardWrapper>
  );
};

export default MetricCard;